import { fmtDateTime } from '@/lib/dates.js';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, Unlock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { studies } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { errorText } from '@/lib/permissionError';


/**
 * CTMS-010 — Study Lock banner.
 *
 * Renders only when `locked_at` is set on the workspace study row.
 * Shows the lock date + lock_reason to every viewer so they know
 * the workspace is read-only; exposes the Unlock button only to
 * platform_admin (any other role cannot clear a lock).
 */
export interface StudyLockBannerProps {
  studyId: string;
  lockedAt: string | null | undefined;
  lockReason: string | null | undefined;
}

export function StudyLockBanner({ studyId, lockedAt, lockReason }: StudyLockBannerProps) {
  if (!lockedAt) return null;
  const { user } = useAuth();
  const qc = useQueryClient();
  const [showUnlock, setShowUnlock] = useState(false);
  const [reason, setReason] = useState('');

  const unlock = useMutation({
    mutationFn: () => studies.unlock(studyId, { reason }),
    onSuccess: () => {
      toast.success('Study unlocked. Read-only mode lifted.');
      setShowUnlock(false);
      setReason('');
      qc.invalidateQueries({ queryKey: ['workspace', studyId] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
    },
    onError: (err) => toast.error(errorText(err, 'Unlock failed')),
  });

  const formattedDate = (() => {
    try { return fmtDateTime(lockedAt); } catch { return lockedAt; }
  })();

  const canUnlock = user?.role === 'platform_admin';

  return (
    <div className="card flex flex-wrap items-start gap-3 border-l-4 border-l-warning-500 bg-warning-50 p-4 text-sm text-warning-900">
      <Lock className="mt-0.5 h-5 w-5 shrink-0 text-warning-700" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 font-semibold">
          <AlertTriangle className="h-4 w-4" aria-hidden />
          <span>Study locked — all data is read-only since {formattedDate}.</span>
        </div>
        <div className="mt-1 text-xs text-warning-800">
          Reason: {lockReason ?? '(no reason recorded)'}
        </div>
        {canUnlock && showUnlock && (
          <div className="mt-3 flex flex-col gap-2">
            <label className="text-[11px] uppercase tracking-wider text-warning-900" htmlFor="unlock-reason">
              Unlock reason (required, 3-2000 chars)
            </label>
            <textarea
              id="unlock-reason"
              className="input min-h-[64px]"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is the study being unlocked?"
              maxLength={2000}
            />
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-primary text-xs"
                disabled={unlock.isPending || reason.trim().length < 3}
                onClick={() => unlock.mutate()}
              >
                <Unlock className="h-3.5 w-3.5" /> Confirm unlock
              </button>
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => { setShowUnlock(false); setReason(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      {canUnlock && !showUnlock && (
        <button
          type="button"
          className="btn-secondary text-xs"
          onClick={() => setShowUnlock(true)}
        >
          <Unlock className="h-3.5 w-3.5" /> Unlock
        </button>
      )}
    </div>
  );
}
